// 90-Second Scalp & Hair Check — Male flow.
// Fast visual cards, minimal text, one question per screen, conditional questions.
//
// IMAGES — MEN ONLY. Every URL below is dedicated to the Men experience.
// It must never overlap with any URL used in the Women flow
// (see /src/data/assessment.js). Each entry is independently editable —
// swap a single URL to update just that one thumbnail.

const IMG = {
  scalp_oil: "https://images.unsplash.com/photo-1583195764036-6dc248ac07d9?w=600&q=80",
  flakes:    "https://images.unsplash.com/photo-1584362917165-526a968579e8?w=600&q=80",
  dry:       "https://images.unsplash.com/photo-1621605815971-fbc98d665033?w=600&q=80",
  flat:      "https://images.unsplash.com/photo-1633675254053-d96c7668c3b8?w=600&q=80",
  hairline:  "https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=600&q=80",
  // Was 1595351298020 — collided with women's density_high; now dedicated male portrait.
  crown:     "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600&q=80",
  // Was 1522337360788 (the pink-haired-girl image); now a dedicated male hair photo.
  shed:      "https://images.unsplash.com/photo-1615109398623-88346a601842?w=600&q=80",
  // Was 1508216310976 — collided with women's coarse; now a dedicated male portrait.
  ok:        "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=600&q=80",
  // Was 1522336572468 — collided with women's hair_wavy / medium_len; now a dedicated male portrait.
  wash:      "https://images.unsplash.com/photo-1502378735452-bc7d86632805?w=600&q=80",
  // Was 1580618672591 — collided with women's chem_color / goal_color; now a dedicated male product photo.
  wax:       "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=600&q=80",
  sweat:     "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=600&q=80",
  hat:       "https://images.unsplash.com/photo-1521369909029-2afed882baee?w=600&q=80",
  air:       "https://images.unsplash.com/photo-1509233725247-49e657c54213?w=600&q=80",
  // Was 1531123897727 — collided with women's hair_coily; now a dedicated male-in-sun portrait.
  sun:       "https://images.unsplash.com/photo-1548142813-c348350df52b?w=600&q=80",
  // Was 1583195764036 (duplicate of scalp_oil); now a dedicated short-haired-man portrait.
  short_h:   "https://images.unsplash.com/photo-1516726817505-f5ed825624d8?w=600&q=80",
  med_h:     "https://images.unsplash.com/photo-1522337094846-8a818192de1f?w=600&q=80",
  long_h:    "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=600&q=80",
  none:      "https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=600&q=80",
};

const O = (id, en, it, image) => ({ id, label: { en, it }, image });

export const MALE_QUESTIONS = [
  {
    id: "notice",
    title: { en: "What do you notice most?", it: "Cosa noti di più?" },
    helper: { en: "Straight to it — what's actually bugging you day to day?", it: "Andiamo dritti — cosa ti dà davvero fastidio ogni giorno?" },
    multi: false,
    options: [
      O("oil_fast", "Scalp becomes oily quickly", "Il cuoio diventa grasso in fretta", IMG.scalp_oil),
      O("flakes", "Visible flakes or residue", "Squame o residui visibili", IMG.flakes),
      O("dry", "Scalp feels dry or uncomfortable", "Cuoio secco o poco confortevole", IMG.dry),
      O("flat", "Hair looks flat or less full", "Capelli piatti o meno pieni", IMG.flat),
      O("front_temples", "Front / temples appear less full", "Fronte / tempie meno piene", IMG.hairline),
      O("crown", "Crown appears less full", "Corona meno piena", IMG.crown),
      O("wash_shed", "More hair during washing or styling", "Più capelli durante lavaggio o styling", IMG.shed),
      O("none", "No major concern — simple maintenance", "Nessuna preoccupazione — manutenzione semplice", IMG.ok),
    ],
  },
  {
    id: "oily_speed",
    title: { en: "How quickly does your scalp feel oily after washing?", it: "Dopo quanto senti il cuoio di nuovo grasso?" },
    helper: { en: "Oil speed alone tells us more about your scalp type than almost anything else you could report.", it: "La velocità con cui torna il sebo dice più sul tuo tipo di cuoio di quasi ogni altra cosa." },
    multi: false,
    options: [
      O("same_day", "The same day", "Lo stesso giorno", IMG.scalp_oil),
      O("next_day", "The next day", "Il giorno dopo", IMG.wash),
      O("2_plus", "After two or more days", "Dopo 2 o più giorni", IMG.ok),
      O("rarely", "Rarely feels oily", "Raramente si sente grasso", IMG.ok),
    ],
  },
  {
    id: "density_area",
    title: { en: "Where do you notice reduced-looking density?", it: "Dove noti una densità dall'aspetto ridotto?" },
    helper: { en: "Pattern matters as much as amount — this pinpoints exactly where your routine should focus.", it: "Il pattern conta quanto la quantità — così sappiamo esattamente dove concentrare la routine." },
    bridge: { en: "Oiliness tells one story, thinning pattern tells another — let's map exactly where.", it: "Il sebo racconta una storia, il pattern di diradamento un'altra — mappiamo esattamente dove." },
    multi: false,
    options: [
      O("hairline", "Front hairline", "Linea frontale", IMG.hairline),
      O("temples", "Temples", "Tempie", IMG.hairline),
      O("crown", "Crown", "Corona", IMG.crown),
      O("overall", "Overall", "Ovunque", IMG.flat),
      O("nowhere", "Nowhere", "Nessuna", IMG.ok),
    ],
  },
  {
    id: "wash_freq",
    title: { en: "How often do you wash your hair?", it: "Ogni quanto lavi i capelli?" },
    helper: { en: "Your rhythm, not a rulebook — we just need to know what it actually is.", it: "Il tuo ritmo, non una regola — dobbiamo solo sapere qual è davvero." },
    multi: false,
    options: [
      O("daily", "Daily", "Ogni giorno", IMG.wash),
      O("alt", "Every other day", "A giorni alterni", IMG.wash),
      O("2_3", "2–3 times weekly", "2–3 volte a settimana", IMG.wash),
      O("less", "Less frequently", "Meno spesso", IMG.wash),
    ],
  },
  {
    id: "affects",
    title: { en: "What regularly affects your scalp?", it: "Cosa incide regolarmente sul tuo cuoio?" },
    helper: { en: "Nothing on this list is too small to matter — check everything that applies.", it: "Niente in questa lista è troppo piccolo per contare — spunta tutto ciò che si applica." },
    bridge: { en: "From what's in your control (routine) to what isn't entirely (everything around you).", it: "Da ciò che controlli (la routine) a ciò che controlli meno (tutto ciò che ti circonda)." },
    multi: true,
    options: [
      O("styling", "Styling wax, gel or spray", "Cera, gel o spray", IMG.wax),
      O("sweat", "Frequent sweating / exercise", "Sudorazione frequente / sport", IMG.sweat),
      O("hats", "Hats or helmets", "Cappelli o caschi", IMG.hat),
      O("dry_air", "Dry indoor air", "Aria secca in casa/ufficio", IMG.air),
      O("sun", "Sun exposure", "Esposizione al sole", IMG.sun),
      O("none", "None of these", "Nessuna di queste", IMG.ok),
    ],
  },
  {
    id: "after_shampoo",
    title: { en: "How does your scalp usually feel after shampooing?", it: "Come senti il cuoio dopo lo shampoo?" },
    helper: { en: "This is the real moment of truth for whether your current shampoo is doing its job.", it: "È il vero momento della verità sul fatto che il tuo shampoo attuale stia facendo il suo lavoro." },
    multi: false,
    options: [
      O("fresh", "Fresh and comfortable", "Fresco e confortevole", IMG.ok),
      O("oily_fast", "Clean but oily again quickly", "Pulito ma di nuovo grasso in fretta", IMG.scalp_oil),
      O("tight", "Tight or dry", "Tirato o secco", IMG.dry),
      O("heavy", "Heavy or not completely clean", "Pesante o non pulito del tutto", IMG.flakes),
      O("variable", "Variable", "Variabile", IMG.wash),
    ],
  },
  {
    id: "current_routine",
    title: { en: "What's your current haircare routine?", it: "Qual è la tua routine attuale?" },
    helper: { en: "Wherever you're starting from — this is just today's baseline, nothing more.", it: "Ovunque tu stia partendo — è solo la base di oggi, niente di più." },
    multi: false,
    options: [
      O("only_shampoo", "Only shampoo", "Solo shampoo", IMG.wash),
      O("sh_conditioner", "Shampoo + conditioner", "Shampoo + balsamo", IMG.wash),
      O("sh_cond_mask", "Shampoo + conditioner + mask", "Shampoo + balsamo + maschera", IMG.wash),
      O("full_routine", "Full routine (+ treatment / serum)", "Routine completa (+ trattamento / siero)", IMG.wash),
    ],
  },
  {
    id: "length",
    title: { en: "How long is your hair?", it: "Quanto sono lunghi i tuoi capelli?" },
    helper: { en: "Length changes what your hair can handle — and what it actually needs from here.", it: "La lunghezza cambia ciò che i capelli tollerano — e cosa gli serve davvero da ora in poi." },
    bridge: { en: "Two quick ones on the hair itself, not just the scalp.", it: "Due veloci sui capelli, non solo sul cuoio." },
    multi: false,
    options: [
      O("very_short", "Very short", "Molto corti", IMG.short_h),
      O("short", "Short", "Corti", IMG.short_h),
      O("medium", "Medium", "Medi", IMG.med_h),
      O("long", "Long", "Lunghi", IMG.long_h),
    ],
  },
  {
    id: "treated",
    title: { en: "Is your hair coloured, bleached, chemically treated or frequently heat-styled?", it: "I tuoi capelli sono colorati, decolorati, trattati chimicamente o spesso stirati?" },
    helper: { en: "Treated hair plays by its own rules — good to know before we build yours.", it: "I capelli trattati hanno le loro regole — meglio saperlo prima di costruire la tua." },
    multi: false,
    options: [
      O("no", "No", "No", IMG.short_h),
      O("yes", "Yes", "Sì", IMG.med_h),
    ],
  },
  // Conditional — only when length is medium/long OR treated=yes
  {
    id: "lengths_feel",
    condition: (a) => ["medium", "long"].includes(a.length) || a.treated === "yes",
    title: { en: "How do your lengths feel today?", it: "Come senti le lunghezze oggi?" },
    helper: { en: "Ends are the oldest part of your hair — they always show wear first.", it: "Le punte sono la parte più vecchia — mostrano l'usura per prime." },
    bridge: { en: "Longer or treated hair earns two more questions — quick as the rest.", it: "Capelli più lunghi o trattati meritano due domande in più — veloci come le altre." },
    multi: false,
    options: [
      O("smooth", "Smooth", "Lisce", IMG.long_h),
      O("dry_ends", "Dry ends", "Punte secche", IMG.dry),
      O("brittle", "Brittle / prone to breakage", "Fragili / si spezzano", IMG.long_h),
    ],
  },
  {
    id: "heat_use",
    condition: (a) => ["medium", "long"].includes(a.length) || a.treated === "yes",
    title: { en: "Do you use heat tools?", it: "Usi strumenti a calore?" },
    helper: { en: "However often you use heat, we'll make sure your routine actually keeps up with it.", it: "Comunque tu usi il calore, ci assicureremo che la routine tenga il passo." },
    multi: false,
    options: [
      O("never", "Never", "Mai", IMG.ok),
      O("some", "Sometimes", "A volte", IMG.wash),
      O("often", "Often", "Spesso", IMG.wax),
    ],
  },
];
